"use client";

import { feature } from "@turf/turf";
import type { Geometry } from "geojson";
import type { SymbolLayerSpecification } from "maplibre-gl";
import { RLayer, RSource } from "maplibre-react-components";
import React, { type ReactElement, useMemo } from "react";
import { useSite } from "@/hooks/sites";
import { ThemeTypeEnum, type Site } from "@/types";
import { getThemePalette } from "@/utils";
import { getColors } from "@oreme/ui/lib/theme";

interface SitesMapLayerGeometryProps {
  site: Site;
}

function SitesMapLayerGeometry({
  site,
}: Readonly<SitesMapLayerGeometryProps>): ReactElement | null {
  const { getSprite } = useSite(site);
  const sourceId = "source-geometry";
  const layerId = "layer-geometry";
  const palette = getThemePalette(getColors(), site.theme as ThemeTypeEnum);

  const sprite = getSprite();

  const paint = useMemo(
    () => ({
      "fill-outline-color": palette[700],
      "fill-color": palette[500],
      "fill-opacity": 0.5,
    }),
    [palette],
  );

  const layout: SymbolLayerSpecification["layout"] = {
    "icon-image": sprite,
    "icon-size": [
      "interpolate",
      ["linear"],
      ["zoom"],
      3,
      0.125,
      10,
      0.25,
      16,
      0.5,
    ],
    "icon-allow-overlap": true,
  };

  const siteFeature = feature(site.geometry as Geometry);
  let layer;

  switch (siteFeature.geometry.type) {
    case "MultiPoint":
      layer = (
        <RLayer
          id={layerId}
          key={layerId}
          source={sourceId}
          type="symbol"
          layout={layout}
        />
      );
      break;
    case "MultiLineString":
      layer = (
        <RLayer
          id={layerId}
          key={layerId}
          source={sourceId}
          type="line"
          layout={{
            "line-join": "round",
            "line-cap": "round",
          }}
          paint={{
            "line-color": palette[700],
            "line-width": 2,
            "line-opacity": 0.8,
          }}
        />
      );
      break;
    case "MultiPolygon":
    default:
      layer = (
        <RLayer
          id={layerId}
          key={layerId}
          paint={paint}
          source={sourceId}
          type="fill"
        />
      );
  }

  return (
    <>
      <RSource
        data={siteFeature}
        generateId
        id={sourceId}
        key={sourceId}
        type="geojson"
      />
      {layer}
    </>
  );
}

export { SitesMapLayerGeometry, type SitesMapLayerGeometryProps };
